import { type CSSProperties, useMemo } from "react";

import clsx from "clsx";
import { sumBy } from "lodash";

import { useUser } from "@components/user-context";
import { formatCurrencyToDollarAmount } from "@features/mmm/utils/formatters";

import { useCellSelection } from "../../../hooks/use-cell-selection";
import {
  FIXED_NAME_COLUMN_ID,
  PACING_STATUS_COLUMN_ID,
  SPEND_THERMOMETER_ID,
  TOTAL_BUDGET_COLUMN_ID,
  TOTAL_BUDGET_PROPOSED_COLUMN_ID,
} from "../constants";
import { MetricDataFormatter } from "../data-type-formatters";
import { type RebudgetingTableRow } from "../mid-cycle-rebudgeting-table";

// This is a simplification of what the actual type injected into the component by @devexpress is.
type Cell = {
  colSpan: number;
  column: {
    type?: unknown;
    column: {
      id: string;
      compareDataLoading: boolean;
      name: string;
      title: string;
    };
  };
};

interface CustomTableHeadWithFixedRowProps {
  readonly isFixed: boolean;
  readonly children: JSX.Element[];
  className?: string;
  style?: CSSProperties;
}

export const FixedRow =
  (tableSummary: RebudgetingTableRow, allRows: RebudgetingTableRow[]) =>
  ({
    isFixed,
    className,
    style,
    children,
  }: CustomTableHeadWithFixedRowProps) => {
    const columnsInView: Cell[] = children[0].props.cells;

    return (
      <thead
        className={clsx(
          {
            "dx-g-bs4-fixed-header": isFixed,
            "dx-g-bs4-table-sticky": isFixed,
            "bg-v2-primary": isFixed,
          },
          className,
        )}
        style={style}
      >
        {children}
        <tr
          style={{
            boxShadow: "0px 2px 4px 0px rgba(2, 18, 48, 0.12)",
          }}
        >
          {columnsInView.map(({ column }: Cell, index: number) => (
            <ColumnHead
              key={index}
              column={column}
              tableSummary={tableSummary}
              rows={allRows}
            />
          ))}
        </tr>
      </thead>
    );
  };

const ColumnHead = ({
  column,
  tableSummary,
  rows,
}: {
  column: Cell["column"];
  tableSummary: RebudgetingTableRow;
  rows: RebudgetingTableRow[];
}) => {
  const { user } = useUser();
  const [editedCells, , { getCell }] = useCellSelection();

  const proposedBudgetValue = useMemo(() => {
    if (editedCells.length > 0) {
      return sumBy(rows, (row) => {
        const editedCell = getCell({
          rowId: row.id,
          columnName: TOTAL_BUDGET_PROPOSED_COLUMN_ID,
        });
        if (editedCell) {
          return editedCell.value;
        } else {
          return row.data.proposedBudgetedSpend;
        }
      });
    } else {
      return tableSummary.data.proposedBudgetedSpend;
    }
  }, [editedCells, getCell, rows, tableSummary]);

  // Not sure why but there's s stub header column
  const columnType = column?.type?.toString();
  if (columnType && ["Symbol(stub)", "Symbol(flex)"].includes(columnType)) {
    return <th className="p-0 table-summary-cell z-2" scope="col"></th>;
  }

  if (columnType === "Symbol(select)") {
    return (
      <th
        className="left-0 p-0 dx-g-bs4-fixed-cell position-sticky z-2"
        scope="col"
      ></th>
    );
  }

  // Only a known data column would have a column property
  if (!column?.column) {
    return null;
  }

  if (column.column.name === FIXED_NAME_COLUMN_ID) {
    const leftOffset = 0;
    return (
      <th
        className="position-sticky z-2"
        scope="col"
        style={{ left: `${leftOffset}px` }}
      >
        <div className="flex items-center h-full px-3">
          <div className="flex flex-col justify-center leading-3">
            <div className="text-xs text-interstellar-1">Total</div>
            <div className="text-xs text-interstellar-1">Remaining</div>
          </div>
        </div>
      </th>
    );
  }

  if (column.column.name === SPEND_THERMOMETER_ID) {
    return <th key={column.column.id} className="z-2" scope="col"></th>;
  }

  if (column.column.name === PACING_STATUS_COLUMN_ID) {
    return <th key={column.column.id} className="z-2" scope="col"></th>;
  }

  if (
    column.column.name === TOTAL_BUDGET_COLUMN_ID ||
    column.column.name === TOTAL_BUDGET_PROPOSED_COLUMN_ID
  ) {
    const actualBudgetTotal = tableSummary.data.budgetedSpend;
    const actualBudgetRemaining = 0;

    const proposedBudgetTotal = proposedBudgetValue;
    const proposedBudgetRemaining = Math.ceil(
      actualBudgetTotal - proposedBudgetTotal,
    );

    const total =
      column.column.name === TOTAL_BUDGET_COLUMN_ID
        ? actualBudgetTotal
        : proposedBudgetTotal;
    const remaining =
      column.column.name === TOTAL_BUDGET_COLUMN_ID
        ? actualBudgetRemaining
        : proposedBudgetRemaining;

    return (
      <th key={column.column.id} className="z-2" scope="col">
        <div className="flex flex-col justify-center">
          <span
            className={clsx("block text-xs text-right pr-4", {
              "text-interstellar-1":
                column.column.name === TOTAL_BUDGET_COLUMN_ID,
              "text-cosmic+3":
                column.column.name === TOTAL_BUDGET_PROPOSED_COLUMN_ID,
            })}
          >
            {formatCurrencyToDollarAmount(
              total,
              user.currencies.currentCurrency,
            )}
          </span>
          <span
            className={clsx("block text-xs text-right pr-4", {
              "text-interstellar-1":
                column.column.name === TOTAL_BUDGET_COLUMN_ID,
              "text-interstellar":
                column.column.name === TOTAL_BUDGET_PROPOSED_COLUMN_ID,
            })}
          >
            {formatCurrencyToDollarAmount(
              remaining,
              user.currencies.currentCurrency,
            )}
          </span>
        </div>
      </th>
    );
  }

  return (
    <th key={column.column.id} className="z-2" scope="col">
      <MetricDataFormatter
        row={tableSummary}
        column={column.column}
        value={null}
      />
    </th>
  );
};
